import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { createBillingCheckout, updateTenant } from '../services/apiService.ts';
import { MODULE_OPTIONS } from '../constants/modules.ts';

interface Props {
  tenant: any | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const checkoutStoragePrefix = 'superadmin:billing-checkout:';
const checkoutBinding = (tenantId: unknown, priceId: string) => `${tenantId}|${priceId.trim()}`;
const checkoutStorageKey = (binding: string) => `${checkoutStoragePrefix}${binding}`;

function loadCheckoutIntent(binding: string): string | null {
  try { return window.sessionStorage.getItem(checkoutStorageKey(binding)); } catch { return null; }
}

function saveCheckoutIntent(binding: string, key: string): void {
  try { window.sessionStorage.setItem(checkoutStorageKey(binding), key); } catch { /* storage is optional */ }
}

function clearCheckoutIntent(binding: string): void {
  try { window.sessionStorage.removeItem(checkoutStorageKey(binding)); } catch { /* storage is optional */ }
}

const EditTenantDialog: React.FC<Props> = ({ tenant, open, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({
    name: '',
    subdomain: '',
    displayName: '',
    city: '',
    country: 'RO',
    status: 'ACTIVE',
    plan: 'BASIC',
    billingState: 'PILOT',
    billingPriceId: '',
    billingSubscriptionId: '',
    billingTrialEndsAt: '',
    stripeCustomerId: '',
    enabledModules: ['RESTAURANT'],
    features: [] as string[],
  });
  const [saving, setSaving] = useState(false);
  const checkoutIntentRef = useRef<{ binding: string; key: string } | null>(null);
  const checkoutFlightRef = useRef(false);
  const [launchingCheckout, setLaunchingCheckout] = useState(false);

  useEffect(() => {
    if (tenant) {
      const billingPriceId = tenant.billingPriceId || '';
      setForm({
        name: tenant.name || tenant.display_name || tenant.displayName || '',
        subdomain: tenant.subdomain || '',
        displayName: tenant.display_name || tenant.displayName || tenant.name,
        city: tenant.city || '',
        country: tenant.country || 'RO',
        status: tenant.status,
        plan: tenant.plan || 'BASIC',
        billingState: tenant.billingState || 'PILOT',
        billingPriceId,
        billingSubscriptionId: tenant.billingSubscriptionId || '',
        billingTrialEndsAt: tenant.billingTrialEndsAt ? String(tenant.billingTrialEndsAt).slice(0, 16) : '',
        stripeCustomerId: tenant.stripeCustomerId || '',
        enabledModules: Array.isArray(tenant.enabledModules)
          ? tenant.enabledModules
          : String(tenant.enabledModules || '').split(',').map((value: string) => value.trim()).filter(Boolean),
        features: Array.isArray(tenant.features)
          ? tenant.features
          : String(tenant.features || '').split(',').map((value: string) => value.trim()).filter(Boolean),
      });
    }
  }, [tenant]);

  const handleChange = (field: string) => (event: any) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleSave = async () => {
    if (!tenant) {
      return;
    }
    try {
      setSaving(true);
      await updateTenant(tenant.id, {
        name: form.name,
        subdomain: form.subdomain,
        displayName: form.displayName,
        description: tenant.description || '',
        contactEmail: tenant.contactEmail || 'noreply@example.com',
        contactPhone: tenant.contactPhone || '',
        address: tenant.address || '',
        city: form.city,
        country: form.country,
        timezone: tenant.timezone || 'Europe/Bucharest',
        logoUrl: tenant.logoUrl || '',
        primaryColor: tenant.primaryColor || '',
        secondaryColor: tenant.secondaryColor || '',
        status: form.status,
        plan: form.plan,
        billingState: form.billingState,
        billingPriceId: form.billingPriceId || null,
        billingSubscriptionId: form.billingSubscriptionId || null,
        billingTrialEndsAt: form.billingTrialEndsAt || null,
        stripeCustomerId: form.stripeCustomerId || null,
        subscriptionEndDate: tenant.subscriptionEndDate || null,
        enabledModules: form.enabledModules,
        features: form.features,
      });
      enqueueSnackbar('Tenant updated', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || error.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getCheckoutBlockingReasons = () => {
    const reasons: string[] = [];
    const currentPriceId = form.billingPriceId.trim();
    const persistedPriceId = (tenant?.billingPriceId || '').trim();
    const blockers = Array.isArray(tenant?.paidPilotBlockers) ? tenant.paidPilotBlockers : [];

    if (!tenant?.id) {
      reasons.push('Tenantul nu este inca incarcat.');
    }
    if (!currentPriceId) {
      reasons.push('billingPriceId este obligatoriu pentru checkout.');
    }
    if (tenant?.billingState === 'SUSPENDED' || tenant?.status === 'SUSPENDED') {
      reasons.push('Tenantul este suspendat comercial si nu poate lansa checkout.');
    }
    if (tenant?.catalogPlanMapped === false) {
      reasons.push('Catalog mapping lipseste pentru priceId-ul curent.');
    }
    if (tenant?.checkoutReady === false) {
      reasons.push('Paid pilot readiness este blocat pe backend.');
      blockers.forEach((blocker: string) => {
        if (blocker && blocker.trim()) {
          reasons.push(blocker.trim());
        }
      });
    }
    if (currentPriceId !== persistedPriceId) {
      reasons.push('Billing price id has local edits. Save and reopen the dialog to refresh readiness.');
    }

    return Array.from(new Set(reasons));
  };

  const checkoutBlockingReasons = getCheckoutBlockingReasons();
  const checkoutDisabled = launchingCheckout || checkoutBlockingReasons.length > 0;

  const handleLaunchCheckout = async () => {
    if (checkoutFlightRef.current) return;
    if (checkoutBlockingReasons.length > 0) {
      enqueueSnackbar(checkoutBlockingReasons[0], { variant: 'warning' });
      return;
    }
    let binding: string | null = null;
    try {
      checkoutFlightRef.current = true;
      setLaunchingCheckout(true);
      binding = checkoutBinding(tenant.id, form.billingPriceId);
      if (checkoutIntentRef.current?.binding !== binding) {
        checkoutIntentRef.current = { binding, key: loadCheckoutIntent(binding) || crypto.randomUUID() };
        saveCheckoutIntent(binding, checkoutIntentRef.current.key);
      }
      const intentKey = checkoutIntentRef.current.key;
      const session = await createBillingCheckout({
        tenantId: tenant.id,
        priceId: form.billingPriceId.trim(),
      }, intentKey);
      const sessionUrl = typeof session?.url === 'string' ? session.url.trim() : '';
      if (!sessionUrl) {
        throw new Error('Stripe checkout response did not include a URL.');
      }
      const checkoutWindow = window.open(sessionUrl, '_blank', 'noopener,noreferrer');
      if (!checkoutWindow) {
        enqueueSnackbar('Browserul nu a deschis fila Stripe. Permite pop-up-uri si reincearca.', { variant: 'warning' });
        return;
      }
      clearCheckoutIntent(binding);
      checkoutIntentRef.current = null;
      enqueueSnackbar('Stripe checkout prepared.', { variant: 'success' });
    } catch (error: any) {
      if (error?.response?.status === 410 && binding) {
        clearCheckoutIntent(binding);
        if (checkoutIntentRef.current?.binding === binding) {
          checkoutIntentRef.current = null;
        }
      }
      const message = typeof error === 'string'
        ? error
        : error?.response?.data?.message || error?.message || 'Unable to launch Stripe checkout.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      checkoutFlightRef.current = false;
      setLaunchingCheckout(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!checkoutFlightRef.current) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Tenant</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12}>
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={handleChange('displayName')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Legacy Plan"
              select
              value={form.plan}
              onChange={handleChange('plan')}
              fullWidth
              helperText="Legacy label and limits only. Gating uses enabledModules, features, and billing state."
            >
              <MenuItem value="BASIC">Basic</MenuItem>
              <MenuItem value="PREMIUM">Premium</MenuItem>
              <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} display="flex" alignItems="center">
            <FormControlLabel
              control={(
                <Switch
                  checked={form.status === 'ACTIVE'}
                  onChange={(event) => setForm({ ...form, status: event.target.checked ? 'ACTIVE' : 'SUSPENDED' })}
                />
              )}
              label={form.status === 'ACTIVE' ? 'Active' : 'Suspended'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Billing State"
              select
              value={form.billingState}
              onChange={handleChange('billingState')}
              fullWidth
              helperText="PILOT and TRIAL stay operationally active. SUSPENDED blocks access."
            >
              <MenuItem value="PILOT">Pilot</MenuItem>
              <MenuItem value="TRIAL">Trial</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Billing Price Id"
              value={form.billingPriceId}
              onChange={handleChange('billingPriceId')}
              fullWidth
              helperText="Stripe price used for subscription checkout."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trial Ends At"
              type="datetime-local"
              value={form.billingTrialEndsAt}
              onChange={handleChange('billingTrialEndsAt')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Stripe Customer Id"
              value={form.stripeCustomerId}
              onChange={handleChange('stripeCustomerId')}
              fullWidth
              helperText="Can stay empty until the first checkout."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Stripe Subscription Id"
              value={form.billingSubscriptionId}
              onChange={handleChange('billingSubscriptionId')}
              fullWidth
              helperText="Usually populated by the billing webhook."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              SelectProps={{ multiple: true }}
              label="Enabled modules"
              value={form.enabledModules}
              onChange={(event) => setForm({
                ...form,
                enabledModules: typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : (event.target.value as string[]),
              })}
              fullWidth
              helperText="These are the real tenant entitlements for product surfaces."
            >
              {MODULE_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Features (comma separated)"
              value={form.features.join(', ')}
              onChange={(event) => setForm({
                ...form,
                features: event.target.value.split(',').map((value) => value.trim()).filter(Boolean),
              })}
              fullWidth
              helperText="Examples: inventory, workforce, reports, housekeeping"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Operational status: <strong>{form.status}</strong> | Billing status: <strong>{form.billingState}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Catalog plan: <strong>{tenant?.catalogPlanCode || 'MANUAL'}</strong>
              {tenant?.catalogPlanMapped === false ? ' | catalog mapping missing' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Catalog source: <strong>{tenant?.catalogPlanSource || 'manual / none'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Checkout readiness: <strong>{tenant?.checkoutReady ? 'ready' : 'blocked'}</strong>
            </Typography>
            {Array.isArray(tenant?.commercialWarnings) && tenant.commercialWarnings.length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {tenant.commercialWarnings.join(' | ')}
              </Typography>
            )}
            {Array.isArray(tenant?.paidPilotBlockers) && tenant.paidPilotBlockers.length > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {tenant.paidPilotBlockers.join(' | ')}
              </Typography>
            )}
            {Array.isArray(tenant?.paidPilotBlockers) && tenant.paidPilotBlockers.length === 0 && tenant?.checkoutReady === false && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                Checkout is blocked, but the backend did not return blocker details yet.
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLaunchCheckout} disabled={checkoutDisabled}>
          {launchingCheckout ? 'Opening Stripe...' : 'Launch Checkout'}
        </Button>
        {checkoutBlockingReasons.length > 0 && (
          <Stack spacing={0.25} sx={{ alignSelf: 'center', maxWidth: 340 }}>
            {checkoutBlockingReasons.map((reason) => (
              <Typography key={reason} variant="caption" color="warning.main">
                {reason}
              </Typography>
            ))}
          </Stack>
        )}
        <Button onClick={onClose} disabled={saving || launchingCheckout}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || launchingCheckout || !form.displayName || !form.name || !form.subdomain}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTenantDialog;
